import React, {useEffect, useState} from "react";
import {useSetting} from "../../contexts/setting";
import {useMediaExport} from "hooks/useMediaExport";
import {useJetCheck} from "hooks/useJetCheck";

import {EYE_FALSE, EYE_TRUE, LOCK_FALSE, LOCK_TRUE, VIDEO, GIF} from "../../data/constants";
import cn from "classnames";
import {useLocation} from "react-router-dom";
import "./index.css";
import {useImages} from "contexts/images";
import {Button} from "biggerui";
import PixelSizeControl from "./PixelSizeControl";
import PixelMaskControl from "./PixelMaskControl";
import CustomBgUpload from "../CustomBgUpload";
import {knownTemplates} from "../../data/templates.js";
import {getSizeFromBase64} from "../../libs/bg-utils";
import {coverFitTransform} from "../../libs/utils";
import {resolveUnlockBgRevert} from "./unlockBgRevert.ts";
import {isMovingMedia, templateOwnImage} from "../BgTransformer/templateBg.ts";
import {useBannerCrop} from "hooks/useBannerCrop";
import {allowsPixelation} from "data/products/index";
import ScopeChips from "./ScopeChips";
import {useSceneScope} from "hooks/useSceneScope";
import {useAuth} from "hooks/useAuth";
import {useFeatures} from "hooks/useFeature";
import type {VideoExportProfileId} from "libs/videoExportProfiles";
import type {VideoExportFormatId} from "libs/videoExportFormats";
import {VideoFormatSelect} from "./VideoFormatSelect.tsx";
import {VideoResolutionSelect} from "./VideoResolutionSelect.tsx";
import {ServerVideoRenderStatus} from "./ServerVideoRenderStatus.tsx";
import {getTemplateInstanceSize} from "../../data/templateManifest.ts";
import "../Tooltip/index.css";

// Jet Check (the star in a banner's header) is parked: the analysis stays
// wired (hooks/useJetCheck, the server route), but the entry point is hidden
// on every channel until it has a proper place in the UI.
const SHOW_JET_CHECK = false;

interface TemplateControlsProps {
  id: string;
  /**
   * The channel whose template list this banner comes from. The page that
   * renders the list names it: the settings context follows the url one
   * render late, so on a channel switch it still names the old channel while
   * the page already draws the new one's banners.
   */
  templateChannel: string;
  hasOnlyOneBanner?: boolean;
  bannerIds?: string[];
  hideLock?: boolean;
}

const TemplateControls = ({
  id,
  templateChannel,
  hasOnlyOneBanner = false,
  bannerIds = [],
  hideLock = false,
}: TemplateControlsProps) => {
  const {
    editVisible,
    editLocked,
    visible,
    locked,
    resetVisible,
    addVisible,
    loadVisible,
    addLocked,
    removeLocked,
    currentVideoPopup,
    editCurrentVideoPopup,
    editIsVideoLoading,
    isVideoLoading,
    videoDuration,
    editVideoDuration,
    isGifLoading,
    videoExportCounter,
    bgOption,
    isAnimationPaused,
    toggleAnimationPaused,
    generativeSpeed,
    channel,
    product,
    pixelationEnabled,
    pixelationShape,
    pixelationBrush,
  } = useSetting();
  const {
    editUploadImages,
    uploadImages,
    isBgAnimation,
    isUploadAnimation,
    customBgs,
    customBgUploaded,
    editCustomBgs,
    editCustomBgUploaded,
    editBgOpacities,
    editBgBrightnesses,
    bgOpacities,
    bgBrightnesses,
    bgOpacity,
    bgBrightness,
    bgPresets,
  } = useImages();
  const {setCrop, dropCrop} = useBannerCrop();
  const location = useLocation();
  const {featureFlags} = useAuth();
  const [videoProfile, setVideoProfile] = useState<VideoExportProfileId>("1080p");
  const [videoFormat, setVideoFormat] = useState<VideoExportFormatId>("mp4");
  const {recordVideo, recordGif, serverVideoStatus} = useMediaExport();
  const {runJetCheck, dismissJetCheck, jetState} = useJetCheck(id);
  const isJetCheckBusy = jetState === "saving" || jetState === "analyzing";

  const isVisible = visible.some((x: string) => x === id);
  const isLocked = locked.some((x: string) => x === id);
  // Role features (data/features.ts). A role without the eye or the chain
  // still sees it on a banner whose state is not the default (excluded,
  // detached), so it can undo what a scene gives it.
  const features = useFeatures();
  const showEye = features["banner.export-eye"] || !isVisible;
  const showChain = features["banner.text-chain"] || isLocked;
  // Scene scopes (libs/sceneScope.ts): the chain gives way to a selection
  // tick and per-dimension chips; a banner with an image of its own gets the
  // per-template upload the chain used to unlock.
  const scope = useSceneScope();
  const isScoped = scope.scoped;
  const isSelected = (scope.selectedTemplates as string[]).includes(id);
  // Only a scopes channel reads the mode table; elsewhere the chain decides.
  const hasOwnImage = isScoped && scope.modeOf(id, "image") === "own";
  const isVideoPopup = currentVideoPopup === id;
  // Per-template mosaic tile size is only meaningful for an unchained (locked)
  // Releases template while the overlay is on — that's when the size override
  // is honoured by PixelationLayer.
  // The settings context is untyped JS; `product` is `{value}` at runtime.
  const productValue = (product as {value?: string} | null)?.value;
  const pixelationOn =
    pixelationEnabled && templateChannel === "releases" && allowsPixelation(productValue);
  // The size, coverage and direction overrides are for the squares only.
  const showPixelSize = pixelationOn && isLocked && pixelationShape === "squares";
  // Any banner has a brush mask; its buttons show while the brush is on.
  const showPixelMask = pixelationOn && pixelationBrush !== "off";
  // Generative backgrounds are always animated (CSS/SVG motion), so the
  // video/GIF export button should appear for them just like for uploaded
  // video/GIF backgrounds.
  const isGenerativeBg = bgOption === "generative";
  // `isBgAnimation` is the shared background's flag. A banner that shows a
  // video or a GIF of its own offers the export too, also in a saved scene
  // that is opened again, where only the shared background is checked.
  const ownImage = templateOwnImage({
    id,
    isLocked,
    bgOption,
    customBgs,
    customBgUploaded,
    imageMode: isScoped ? scope.modeOf(id, "image") : undefined,
  });
  const showAnimationExport =
    isBgAnimation || isUploadAnimation || isGenerativeBg || isMovingMedia(ownImage);
  // A preset an admin saved into the default scene shapes this banner's
  // background while its chain is intact — visible on the canvas, so it gets
  // a marker on the chain rather than staying a hidden state. Only shown
  // over a numbered background: a shared upload replaces preset images.
  const preset = bgPresets?.[id];
  const hasBgPreset =
    !isLocked &&
    Number.isInteger(Number(bgOption)) &&
    Number(bgOption) > 0 &&
    Boolean(
      preset &&
      (preset.customBg !== undefined ||
        preset.opacity !== undefined ||
        preset.brightness !== undefined)
    );
  const lockClass = cn("lock checkbox", {locked: isLocked, "has-bg-preset": hasBgPreset});
  const visibleClass = cn("eye checkbox", {visible: isVisible});
  const videoClass = cn("videoPopupCheckbox checkbox", {open: isVideoPopup});
  const eyeTitle = isVisible ? "Shift+click: only this export" : "Shift+click: all to export";
  const lockTitle = isLocked
    ? "Resync text"
    : hasBgPreset
      ? "Detach text to edit. This banner has a background preset from the defaults."
      : "Detach text to edit";
  const videoTitle = isVideoPopup ? "Close" : "Export video/GIF";

  // Shift+click handler for mass visibility toggle.
  //
  // The template's channel is the list's own, not the path's or the
  // context's: a product that carries its offering across channels draws
  // banners the entry channel does not list, /scene/:id has no channel in the
  // path at all, and the context lags the url on a channel switch.
  const pageType = templateChannel;
  const serverVideoExportEnabled = featureFlags.videoExport === true;
  let videoSourceSize: {width: number; height: number} | null = null;
  if (serverVideoExportEnabled) {
    if (typeof pageType !== "string" || pageType.length === 0) {
      throw new Error("Cannot resolve the channel for server video export");
    }
    videoSourceSize = getTemplateInstanceSize(pageType, id, productValue);
  }

  const handleVideoPopup = (bannerId: string, open: boolean) => {
    editCurrentVideoPopup(open ? bannerId : "");
  };

  const handleClick = (e: React.MouseEvent<HTMLLabelElement>) => {
    if (!e.shiftKey || hasOnlyOneBanner) return; // only process Shift+click for multi-banner pages
    // Prevent default checkbox toggle; we'll handle visibility manually
    e.preventDefault();
    e.stopPropagation();

    if (isVisible) {
      // Mass hide: keep only current banner visible for export
      resetVisible();
      addVisible(id);
    } else {
      // Mass show: make all banners visible for export
      const allIds = bannerIds.length ? bannerIds : [id];
      if (allIds.length) {
        loadVisible(allIds);
      } else {
        // Fallback: at least make current banner visible
        addVisible(id);
      }
    }
  };

  const handleLockClick = (bannerId: string, isNowLocked: boolean) => {
    if (isNowLocked) {
      addLocked(bannerId, isNowLocked);
      const mainImage = uploadImages?.main;
      const templateImage = uploadImages?.[bannerId];

      if (!templateImage) {
        editUploadImages(bannerId, mainImage, true);
      }
      // Detaching freezes the banner's background look: whatever opacity and
      // brightness it shows now become its own, so the shared sliders stop
      // reaching it. Its own sliders sit in its background tool panel. A
      // preset value it already carries is kept as is.
      editBgOpacities(bannerId, bgOpacities?.[bannerId] ?? bgOpacity ?? 100);
      editBgBrightnesses(bannerId, bgBrightnesses?.[bannerId] ?? bgBrightness ?? 100);
    } else {
      removeLocked(bannerId, isNowLocked);
      editUploadImages(bannerId, undefined, true);
      // The template's own slider values belong to its detached state;
      // re-synced, it returns to its preset from the default scene when it
      // has one, and to the shared sliders otherwise.
      editBgOpacities(bannerId, bgPresets?.[bannerId]?.opacity);
      editBgBrightnesses(bannerId, bgPresets?.[bannerId]?.brightness);

      // Detaching → re-syncing to the shared background: a per-template
      // upload must not linger, or re-locking later resurrects it (with its
      // stale cover-fit transform). Runs ONLY when this template actually
      // carries its own upload — the same transform records hold the user's
      // pan/zoom of the shared/baked background, which unlock must not reset.
      // See resolveUnlockBgRevert for the Livestream shared-vs-own subtlety.
      const revert = resolveUnlockBgRevert({
        channelValue: channel?.value,
        customBgs,
        customBgUploaded,
        templateId: bannerId,
      });
      if (revert.action === "revert-to-shared") {
        // Livestream renders customBgs[id] directly, so rewrite the slot to
        // the shared copy and re-cover-fit it to this template's aspect (the
        // per-template upload had overwritten the transform).
        const sharedBg = revert.sharedBg;
        editCustomBgs(bannerId, sharedBg, true);
        editCustomBgUploaded(bannerId, true);
        (async () => {
          try {
            const {width, height} = await getSizeFromBase64(sharedBg);
            const template = (knownTemplates.livestream || []).find((t) => t.id === bannerId);
            if (!template) {
              throw new Error(`Unknown livestream template "${bannerId}"`);
            }
            const {scale, x, y} = coverFitTransform(width, height, template.width, template.height);
            setCrop(bannerId, {x, y, scale, rotate: 0, flip: {x: false, y: false}});
          } catch {
            /* leave the transform as-is if the shared image can't be measured */
          }
        })();
      } else if (revert.action === "clear") {
        // Drop the per-template upload + transform; the template falls back to
        // its preset image when the default scene gave it one, else to the
        // shared/gallery/baked background.
        editCustomBgs(bannerId, bgPresets?.[bannerId]?.customBg, true);
        editCustomBgUploaded(bannerId, false);
        dropCrop(bannerId);
      }
    }
  };

  useEffect(() => {
    if (hasOnlyOneBanner && !isVisible) {
      addVisible(id);
    }
  }, [addVisible, hasOnlyOneBanner, id, isVisible]);

  // For generative backgrounds the exported clip must span exactly one CSS
  // animation loop (generativeSpeed, in ms) to loop seamlessly. Pin the
  // duration field to the animation period whenever the export popup is open
  // for a generative background, and keep it in sync if the speed changes.
  useEffect(() => {
    if (isVideoPopup && isGenerativeBg) {
      editVideoDuration(String(generativeSpeed / 1000));
    }
  }, [isVideoPopup, isGenerativeBg, generativeSpeed, editVideoDuration]);

  // A paused generative animation would record as a static frame. Resume it
  // before capturing so the exported video/GIF shows the motion.
  const resumeGenerativeAnimation = () => {
    if (isGenerativeBg && isAnimationPaused) {
      toggleAnimationPaused();
    }
  };

  const handleVideoExport = async () => {
    resumeGenerativeAnimation();
    await recordVideo(id, pageType, videoProfile, videoFormat);
  };

  const handleGifExport = async () => {
    resumeGenerativeAnimation();
    await recordGif(id, pageType);
  };

  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && currentVideoPopup === id) {
        editCurrentVideoPopup("");
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [currentVideoPopup, id, editCurrentVideoPopup]);

  useEffect(() => {
    editCurrentVideoPopup("");
  }, [location.pathname, location.search, location.hash]);

  let jetCheckStatus: React.ReactNode = null;
  if (jetState === "saving") {
    jetCheckStatus = (
      <div className="jet-check-status">
        <span className="jet-check-spinner-sm" />
        <span>Saving scene...</span>
      </div>
    );
  } else if (jetState === "analyzing") {
    jetCheckStatus = (
      <div className="jet-check-status">
        <span className="jet-check-spinner-sm" />
        <span>Rendering & analyzing...</span>
      </div>
    );
  } else if (jetState && "url" in jetState) {
    jetCheckStatus = (
      <div className="jet-check-status ready">
        <a href={jetState.url} target="_blank" rel="noopener noreferrer">
          View Jet Check analysis
        </a>
        <button className="jet-check-dismiss" onClick={dismissJetCheck}>
          &times;
        </button>
      </div>
    );
  } else if (jetState && "error" in jetState) {
    jetCheckStatus = (
      <div className="jet-check-status error">
        <span title={jetState.error}>Jet Check: {jetState.error}</span>
        <button className="jet-check-dismiss" onClick={dismissJetCheck}>
          &times;
        </button>
      </div>
    );
  }

  return (
    <div className={cn("template-controls", {popup: isVideoPopup, single: hasOnlyOneBanner})}>
      {!isVideoPopup ? (
        <>
          {!hasOnlyOneBanner && (
            <>
              {isScoped && (
                <button
                  type="button"
                  className={cn("checkbox select scope-select", {selected: isSelected})}
                  aria-pressed={isSelected}
                  aria-label="Include this banner in the selection"
                  data-tooltip={
                    isSelected
                      ? "In the selection: sidebar edits reach this banner"
                      : "Add to selection: sidebar edits then reach only selected banners"
                  }
                  onClick={() => scope.toggleSelected(id)}
                >
                  {/* A circle that fills with a tick, like the round controls
                      beside it. The tick sits well inside the circle, so the
                      checked state reads as a check, not as a radio dot. */}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle
                      cx="12"
                      cy="12"
                      r="8.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      fill={isSelected ? "currentColor" : "none"}
                    />
                    {isSelected && (
                      <path
                        d="M8.2 12.4l2.6 2.6L16 9.6"
                        stroke="#222325"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </svg>
                </button>
              )}
              {isScoped && <ScopeChips id={id} />}
              {/* Per-template custom-bg upload — only on a detached template:
                  a locked chain, or an image of its own on a scopes channel.
                  This is the only gate. Multi-template only. */}
              {(isLocked || hasOwnImage) && <CustomBgUpload id={id} />}
              {showEye && (
                <>
                  <input
                    className="template-checkbox"
                    type="checkbox"
                    id={`visibility-${id}`}
                    checked={isVisible}
                    onChange={() => editVisible(id, !isVisible)}
                  />
                  <label
                    data-tooltip={eyeTitle}
                    htmlFor={`visibility-${id}`}
                    className={visibleClass}
                    onClick={handleClick}
                  >
                    {isVisible ? <img src={EYE_TRUE} alt="" /> : <img src={EYE_FALSE} alt="" />}
                  </label>
                </>
              )}
              {!hideLock && !isScoped && showChain && (
                <>
                  <input
                    className="template-checkbox"
                    type="checkbox"
                    id={`lock-${id}`}
                    checked={isLocked}
                    onChange={() => handleLockClick(id, !isLocked)}
                  />
                  <label data-tooltip={lockTitle} htmlFor={`lock-${id}`} className={lockClass}>
                    {isLocked ? <img src={LOCK_TRUE} alt="" /> : <img src={LOCK_FALSE} alt="" />}
                  </label>
                </>
              )}
              {showPixelSize && <PixelSizeControl id={id} />}
              {showPixelMask && <PixelMaskControl id={id} />}
            </>
          )}
          {SHOW_JET_CHECK && (
            <button
              className={cn("checkbox jet-check", {loading: isJetCheckBusy})}
              data-tooltip="Jet Check"
              onClick={runJetCheck}
              disabled={isJetCheckBusy}
            >
              {isJetCheckBusy ? (
                <span className="jet-check-spinner" />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10 1L12.2 6.6L18 7.2L13.8 11L15 17L10 14L5 17L6.2 11L2 7.2L7.8 6.6L10 1Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinejoin="round"
                    fill="none"
                  />
                  <path
                    d="M7 10.5L9.5 13L13.5 8"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          )}
          {SHOW_JET_CHECK && jetCheckStatus}
        </>
      ) : (
        <div className="video-popup-controls">
          <label htmlFor="video-seconds">Duration in seconds:</label>
          <input
            className="video-seconds"
            id="video-seconds"
            type="text"
            value={videoDuration}
            onChange={(e) => editVideoDuration(e.target.value)}
            title={
              isGenerativeBg
                ? "Defaults to one animation loop for a seamless GIF — edit to record longer/shorter"
                : undefined
            }
          />
          {serverVideoExportEnabled && videoSourceSize && features["export.video-quality"] && (
            <>
              <VideoResolutionSelect
                value={videoProfile}
                onChange={setVideoProfile}
                sourceSize={videoSourceSize}
              />
              <VideoFormatSelect value={videoFormat} onChange={setVideoFormat} />
            </>
          )}
          <Button
            className="btn"
            onClick={handleVideoExport}
            id={"export_video_button"}
            disabled={isVideoLoading || isGifLoading}
            progress={serverVideoExportEnabled ? undefined : videoExportCounter.video}
            loading={isVideoLoading && !serverVideoExportEnabled}
          >
            {isVideoLoading ? (
              serverVideoExportEnabled ? (
                <ServerVideoRenderStatus status={serverVideoStatus} />
              ) : (
                <span className="loading-label">Recording video</span>
              )
            ) : (
              <span>Get video</span>
            )}
          </Button>

          <Button
            className="btn"
            onClick={handleGifExport}
            id={"export_gif_button"}
            disabled={isVideoLoading || isGifLoading}
            loading={isGifLoading}
            progress={videoExportCounter.pregif || videoExportCounter.gif}
          >
            {isGifLoading ? (
              <span>{videoExportCounter.gif ? "Composing GIF " : "Recording frames"}</span>
            ) : (
              <span>Get GIF</span>
            )}
          </Button>
        </div>
      )}

      {showAnimationExport && features["export.animated"] && (
        <>
          <input
            className="template-checkbox"
            type="checkbox"
            id={`video-${id}`}
            checked={isVideoPopup}
            onChange={() => handleVideoPopup(id, !isVideoPopup)}
          />
          <label data-tooltip={videoTitle} htmlFor={`video-${id}`} className={videoClass}>
            {isVideoPopup ? "< Back" : <img src={VIDEO} alt="" />}
          </label>
        </>
      )}
    </div>
  );
};

export default TemplateControls;

export function ExportVisibilityControl({id, visible, disabled, onChange}: {id: string; visible: boolean; disabled?: boolean; onChange: (visible: boolean) => void}) {
  return <><input id={`visibility-${id}`} className="template-checkbox" type="checkbox" checked={visible} disabled={disabled} onChange={() => onChange(!visible)} /><label htmlFor={`visibility-${id}`} className="checkbox eye" data-tooltip="Include in export"><img src={visible ? EYE_TRUE : EYE_FALSE} alt={visible ? "Included in export" : "Excluded from export"} /></label></>;
}
